import React, { ReactNode } from "react";
import styled from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  DisplayAmount,
  FigureLarge,
  InfoLabel,
  Meta,
} from "features/affiliation/NewLook/roles";
import { AFFILIATION_CONTAINER } from "features/affiliation/NewLook/newLookUtils";

export interface SummaryLead {
  label: ReactNode;
  value: number;
  /** "$" for amounts; counts have none. */
  currency?: string;
  meta?: ReactNode;
  tooltip?: ReactNode;
  /** Next to the figure, e.g. "Create payout". */
  action?: ReactNode;
}

export interface SummaryItem {
  key: string;
  label: ReactNode;
  value: ReactNode;
  meta?: ReactNode;
  tooltip?: ReactNode;
}

interface TabSummaryProps {
  lead: SummaryLead;
  /** Secondary figures, quieter, on the well beside the lead. */
  items: SummaryItem[];
  loading?: boolean;
  "data-testid"?: string;
}

/**
 * The top of an affiliation tab: one lead figure in the display role, the
 * tab's other figures beside it, smaller and on the inset well (the same
 * split as the dashboard hero). On phones the figures become rows.
 */
const TabSummary = ({
  lead,
  items,
  loading,
  "data-testid": testId,
}: TabSummaryProps) => (
  <Panel flush={true} data-testid={testId}>
    <Layout $count={items.length}>
      <Lead>
        <InfoLabel
          tooltip={lead.tooltip}
          analyticsFieldName={
            typeof lead.label === "string" ? lead.label : "lead"
          }
        >
          {lead.label}
        </InfoLabel>
        <LeadRow>
          {loading ? (
            <Skeleton width={180} height={40} />
          ) : (
            <DisplayAmount value={lead.value} currency={lead.currency} />
          )}
          {lead.action}
        </LeadRow>
        {lead.meta && <Meta>{lead.meta}</Meta>}
      </Lead>
      {items.map((item) => (
        <Item key={item.key}>
          <InfoLabel
            tooltip={item.tooltip}
            analyticsFieldName={
              typeof item.label === "string" ? item.label : item.key
            }
          >
            {item.label}
          </InfoLabel>
          <FigureLarge>
            {loading ? <Skeleton width={96} height={22} /> : item.value}
          </FigureLarge>
          {item.meta && <Meta>{item.meta}</Meta>}
        </Item>
      ))}
    </Layout>
  </Panel>
);

export default TabSummary;

// Clipped to the panel's inner radius, so the well reaches its corners.
const Layout = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns:
    minmax(260px, 1.5fr)
    repeat(${(p) => p.$count}, minmax(0, 1fr));
  overflow: hidden;
  border-radius: 11px;
  @container ${AFFILIATION_CONTAINER} (max-width: 760px) {
    grid-template-columns: repeat(
      ${(p) => (p.$count <= 3 ? p.$count : 2)},
      minmax(0, 1fr)
    );
  }
  @container ${AFFILIATION_CONTAINER} (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Lead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 20px 24px;
  @container ${AFFILIATION_CONTAINER} (max-width: 760px) {
    grid-column: 1 / -1;
    padding: 16px 16px 16px 20px;
  }
`;
const LeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
`;
/* The secondary figures sit on the well, like the dashboard hero's side;
   one row each on phones (label and meta left, figure right). */
const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 20px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  @container ${AFFILIATION_CONTAINER} (max-width: 760px) {
    padding: 14px 16px 14px 20px;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    &:nth-child(2) {
      border-left: none;
    }
  }
  @container ${AFFILIATION_CONTAINER} (max-width: 560px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas: "label figure" "meta figure";
    align-items: center;
    column-gap: 12px;
    row-gap: 2px;
    padding: 12px 16px 12px 20px;
    border-left: none;
    > :nth-child(1) {
      grid-area: label;
    }
    > :nth-child(2) {
      grid-area: figure;
      font-size: 17px;
      line-height: 22px;
    }
    > :nth-child(3) {
      grid-area: meta;
    }
  }
`;
